import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cn } from "../utils";
import { navigationTabsTriggerVariants, navigationTabsListVariants } from "./navigation-tabs.variants";
import type { NavigationTabsProps, NavigationTabsListProps, NavigationTabsTriggerProps, NavigationTabsContentProps } from "./navigation-tabs.types";

/**
 * NavigationTabs Component
 *
 * V&A Waterfront Design System - Navigation Tabs
 * Figma: https://www.figma.com/design/CV2ePEn9S0SSZ7T3AAPqF4/V-A-Waterfront---Website---Foundation-Design-System?node-id=10525-24467
 *
 * A set of layered sections of content—known as tab panels—that are displayed
 * one at a time. Built on Radix UI Tabs for accessibility and keyboard navigation.
 *
 * @example
 * // Basic usage
 * <NavigationTabs defaultValue="tab1">
 *   <NavigationTabsList>
 *     <NavigationTabsTrigger value="tab1">Tab 1</NavigationTabsTrigger>
 *     <NavigationTabsTrigger value="tab2">Tab 2</NavigationTabsTrigger>
 *   </NavigationTabsList>
 *   <NavigationTabsContent value="tab1">Content 1</NavigationTabsContent>
 *   <NavigationTabsContent value="tab2">Content 2</NavigationTabsContent>
 * </NavigationTabs>
 *
 * @example
 * // Controlled mode
 * const [activeTab, setActiveTab] = useState("tab1");
 * <NavigationTabs value={activeTab} onValueChange={setActiveTab}>
 *   ...
 * </NavigationTabs>
 */
const NavigationTabs = React.forwardRef<React.ComponentRef<typeof TabsPrimitive.Root>, NavigationTabsProps>((t0, ref) => {
  const $ = _c(11);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("w-full", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== ref || $[9] !== t1) {
    t2 = <TabsPrimitive.Root ref={ref} className={t1} {...props}>{children}</TabsPrimitive.Root>;
    $[6] = children;
    $[7] = props;
    $[8] = ref;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
});
NavigationTabs.displayName = "NavigationTabs";

/**
 * NavigationTabsList Component
 *
 * Container for the tab triggers. Renders as role="tablist".
 */
const NavigationTabsList = React.forwardRef<React.ComponentRef<typeof TabsPrimitive.List>, NavigationTabsListProps>((t0, ref) => {
  const $ = _c(11);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn(navigationTabsListVariants(), className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== ref || $[9] !== t1) {
    t2 = <TabsPrimitive.List ref={ref} className={t1} {...props}>{children}</TabsPrimitive.List>;
    $[6] = children;
    $[7] = props;
    $[8] = ref;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
});
NavigationTabsList.displayName = "NavigationTabsList";

/**
 * NavigationTabsTrigger Component
 *
 * Individual tab button. Supports multiple states: default, hover, active, disabled.
 * Uses pill-shaped styling with rounded-full border radius.
 */
const NavigationTabsTrigger = React.forwardRef<React.ComponentRef<typeof TabsPrimitive.Trigger>, NavigationTabsTriggerProps>((t0, ref) => {
  const $ = _c(16);
  let children;
  let className;
  let disabled;
  let props;
  let size;
  if ($[0] !== t0) {
    ({
      className,
      size,
      children,
      disabled,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = disabled;
    $[4] = props;
    $[5] = size;
  } else {
    children = $[1];
    className = $[2];
    disabled = $[3];
    props = $[4];
    size = $[5];
  }
  let t1;
  if ($[6] !== className || $[7] !== size) {
    t1 = cn(navigationTabsTriggerVariants({
      size,
      className
    }));
    $[6] = className;
    $[7] = size;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const t2 = disabled || undefined;
  let t3;
  if ($[9] !== children || $[10] !== disabled || $[11] !== props || $[12] !== ref || $[13] !== t1 || $[14] !== t2) {
    t3 = <TabsPrimitive.Trigger ref={ref} className={t1} disabled={disabled} data-disabled={t2} {...props}>{children}</TabsPrimitive.Trigger>;
    $[9] = children;
    $[10] = disabled;
    $[11] = props;
    $[12] = ref;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
});
NavigationTabsTrigger.displayName = "NavigationTabsTrigger";

/**
 * NavigationTabsContent Component
 *
 * Content panel for each tab. Only the active tab's content is rendered.
 */
const NavigationTabsContent = React.forwardRef<React.ComponentRef<typeof TabsPrimitive.Content>, NavigationTabsContentProps>((t0, ref) => {
  const $ = _c(11);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("mt-4 focus:outline-none", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== ref || $[9] !== t1) {
    t2 = <TabsPrimitive.Content ref={ref} className={t1} {...props}>{children}</TabsPrimitive.Content>;
    $[6] = children;
    $[7] = props;
    $[8] = ref;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
});
NavigationTabsContent.displayName = "NavigationTabsContent";
export { NavigationTabs, NavigationTabsList, NavigationTabsTrigger, NavigationTabsContent };